import * as d3 from 'd3';
import * as idb from 'idb';

import type { Taxon } from '@/ebird/taxonomy';
import type { Checklist, Location, Observation } from '@/ebird/ebird';
import type * as dateish from '@/dateish';

export class Store {
  constructor(
    readonly db: DB,
    readonly taxonomy: TaxonomyDB
  ) {}

  async allObservations(): Promise<Observation[]> {
    return await this.db.getAll("observations");
  }

  async doyObservations(doy: dateish.DoY): Promise<Observation[]> {
    const all = await this.allObservations(); // TODO: filter directly
    return all.filter(o => `${o.date.month}-${o.date.day}` === doy);
  }

  async doySpecies(doy: dateish.DoY): Promise<Observation[]> {
    const observations = await this.doyObservations(doy);
    return observations.filter(o => o.taxon.speciesCode != null);
  }

  async doySpeciesTable(doy: dateish.DoY): Promise<SpeciesTable> {
    return this.getSpeciesTable(o => `${o.date.month}-${o.date.day}` === doy);
  }

  async moySpeciesTable(moy: dateish.MoY): Promise<SpeciesTable> {
    return this.getSpeciesTable(o => o.date.month === moy);
  }

  async getSpeciesTable(fn: ((o: Observation) => boolean)): Promise<SpeciesTable> {
    const all = await this.allObservations(); // TODO: Get the year extents in a better way
    const [minYear, maxYear] = d3.extent(all, o => o.date.year);

    const observations = all.filter(fn)
      .filter(o => o.taxon.speciesCode != null);

    const table: Record<string, Set<number>> = {};
    for (const o of observations) {
      table[o.taxon.speciesCode!] ??= new Set();
      table[o.taxon.speciesCode!].add(o.date.year);
    }

    return {
      years: { min: minYear!, max: maxYear! },
      species: Object.entries(table).map(([ code, years ]) => ({ code, years })),
    }
  }

  async clearObservations(): Promise<unknown> {
    await this.db.clear("observations");
    return;
  }

  async importObservations(file: File): Promise<unknown> {
    await this.clearObservations(); // clear existing first...

    const rows = await parseRows(file);
    console.log('import-file: rows:', rows.length);

    // TODO: leverage more d3 time and format stuff
    // https://github.com/d3/d3-time
    // https://github.com/d3/d3-time-format#locale_format
    const parseDate = d3.timeParse("%Y-%m-%d");

    const observations: Observation[] = rows.map(r => {
      const checklist: Checklist = {
        id: r.checklistID,
        date: r.date,
        time: r.time,
        protocol: r.protocol,
        complete: !!parseInt(r.complete), // TODO: NaN
        duration: parseInt(r.durationMin), // TODO: NaN
        distance: parseFloat(r.distanceKM), // TODO: NaN
        area: parseFloat(r.areaHA), // TODO: NaN
        observers: parseInt(r.numObservers),
        comments: r.checklistComments,
      };

      const location: Location = {
        id: r.locationID,
        name: r.locationName,
        state: r.stateProvince,
        county: r.county,
        latitude: parseFloat(r.latitude),
        longitude: parseFloat(r.longitude),
      };

      const when = parseDate(checklist.date);
      if (!when) {
        throw new Error(`when: failed to parse ${checklist.date}`)
      }

      const [ year, month, day ] = checklist.date.split("-"); // TODO: Parse for real

      const observation: Observation = {
        checklist,
        location,
        taxon: this.taxonomy.scientific(r.scientificName),
        count: parseInt(r.count), // TODO: NaN/X
        details: r.observationDetails,
        date: {
          year: parseInt(year),
          month: month as dateish.MoY,
          day: day as dateish.DoY,
          when
        },
      };
      return observation;
    });

    const tx = this.db.transaction('observations', 'readwrite');
    const promises: Promise<any>[] = observations.map(o => tx.store.add(o));
    promises.push(tx.done);
    await Promise.all(promises);

    // TODO: record for imports

    return; // TODO
  }
}

export interface SpeciesTable {
  years: { min: number, max: number }
  species: { code: string, years: Set<number> }[]
}

export async function initStore(taxa: Taxon[]): Promise<Store> {
  const db = await openDB();
  const taxonomy = new TaxonomyDB(taxa);
  return new Store(db, taxonomy);
}

export interface Schema extends idb.DBSchema {
  observations: {
    key: number
    value: Observation
  }
  imports: {
    key: number
    value: unknown
  }
}

export type DB = idb.IDBPDatabase<Schema>

export async function parseRows(file: File): Promise<ObservationRow[]> {
  const text = await file.text();

  // Template row that can be reused in the parser below
  const row = Object.fromEntries(Columns.map(c => [c, ''])) as ObservationRow;
  return d3.csvParseRows(text, (r, i) => {
    if (i === 0) { return; }
    r.forEach((val, j) => row[Columns[j]] = val);
    return structuredClone(row);
  });
}

type ObservationRow = Record<typeof Columns[number], string>;

// MyEBirdData.csv Headers
const Columns = [
  'checklistID', // 0 "Submission ID",
  'commonName', //  1 "Common Name",
  'scientificName', //  2 "Scientific Name",
  'taxonOrder', //  3 "Taxonomic Order",
  'count', //  4 "Count",
  'stateProvince', //  5 "State/Province",
  'county', //  6 "County",
  'locationID', //  7 "Location ID",
  'locationName', //  8 "Location",
  'latitude', //  9 "Latitude",
  'longitude', // 10 "Longitude",
  'date', // 11 "Date",
  'time', // 12 "Time",
  'protocol', // 13 "Protocol",
  'durationMin', // 14 "Duration (Min)",
  'complete', // 15 "All Obs Reported",
  'distanceKM', // 16 "Distance Traveled (km)",
  'areaHA', // 17 "Area Covered (ha)",
  'numObservers', // 18 "Number of Observers",
  'breedingCode', // 19 "Breeding Code",
  'observationDetails', // 20 "Observation Details",
  'checklistComments', // 21 "Checklist Comments",
  'macaulayIDs', // 22 "ML Catalog Numbers"
] as const;

async function openDB(): Promise<DB> {
  // TODO: Figure this out...
  // await idb.deleteDB('birding-days');

  return await idb.openDB<Schema>('birding-days', 1, {
    upgrade(db, oldVersion, newVersion, transaction, event) {
      console.warn(`todo:db.upgrade old:${oldVersion} new:${newVersion}`);
      if (oldVersion < 1) {
        // Observations don't have an intrinsic identifier
        db.createObjectStore('imports', { autoIncrement: true });
        db.createObjectStore('observations', { keyPath: 'id', autoIncrement: true });
      }
      // if (oldVersion < 2) {
      //   db.createObjectStore('checklists', { keyPath: 'id' });
      //   db.createObjectStore('locations', { keyPath: 'id' });
      // }
    },
    blocked(currentVersion, blockedVersion, event) {
      console.warn(`todo:db.blocked current:${currentVersion} blocked:${blockedVersion}`);
    },
    blocking(currentVersion, blockedVersion, event) {
      console.warn(`todo:db.blocking current:${currentVersion} blocked:${blockedVersion}`);
    },
    terminated() {
      console.warn(`todo:db.terminated`);
    },
  });
}

export class TaxonomyDB {
  private idxCode: Map<string, Taxon>
  private idxSci: Map<string, Taxon>

  constructor(private taxa: Taxon[]) {
    this.idxCode = d3.index(taxa, t => t.taxonCode);
    this.idxSci = d3.index(taxa, t => t.scientificName);
  }

  code(id: string): Taxon {
    const taxon = this.idxCode.get(id);
    if (taxon) { return taxon; }
    throw new Error(`not found: code=${id}`);
  }
  scientific(name: string): Taxon {
    const taxon = this.idxSci.get(name);
    if (taxon) { return taxon; }
    throw new Error(`not found: scientific=${name}`);
  }
}